// ============================================================================
// MY SUBMISSIONS VIEW
//
// For gp_employee role:
// - Shows only their own submitted referrals (metrics, not audit logs)
// - Server-side pagination for performance
// - Responsive layout (cards on mobile)
// - Basic analytics on their personal submissions
// - Friendly error handling for missing Firestore indexes
//
// Columns: Date | Referral ID | Specialty | Pack | Outcome | Status | View
// "View" opens a ReceiptModal showing the exact same layout as the PDF receipt.
// ============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FileText, AlertTriangle, CheckCircle, XCircle,
  Calendar, Clock, RefreshCw, Filter, AlertCircle as AlertIcon,
  Eye, Copy, Check, PauseCircle, Search, X,
} from 'lucide-react';
import { getApp } from 'firebase/app';
import { getFirestore, collection, query, getDocs, orderBy } from 'firebase/firestore';
import {
  getMetricsByUser,
  getSpecialties,
  getReferral,
  getPacks,
  getPatientIdentity,
  getContact,
  getInsurance,
  listAttachments,
  getWorkflowState,
  getTenantUser,
} from '../core/index.js';
import PATHS from '../core/firestorePaths.js';
import { buildFullReceiptModel } from '../core/receipt/buildFullReceiptModel.js';
import { useAuth } from "../auth/AuthContext.jsx";
import { ResponsiveTable, PaginationControls } from '../components/VirtualizedTable.jsx';
import ReceiptModal from '../components/ReceiptModal.jsx';

// Date range presets
const DATE_RANGES = [
  { id: 'today', label: 'Today',        days: 0    },
  { id: '7d',    label: 'Last 7 days',  days: 7    },
  { id: '30d',   label: 'Last 30 days', days: 30   },
  { id: 'all',   label: 'All time',     days: null },
];

// Outcome filter options (must match outcomeStatus values in Firestore)
const OUTCOME_FILTERS = [
  { id: '',         label: 'All Outcomes' },
  { id: 'routine',  label: 'Routine'      },
  { id: 'urgent',   label: 'Urgent'       },
  { id: 'not_yet',  label: 'Not Yet'      },
];

// ── Pipeline filter tabs ──────────────────────────────────────────────────────
// Each tab filters the current page client-side via workflowMap.
// action_needed = GP must act; in_progress = ops pipeline moving; completed = done.
const STATUS_TABS = [
  {
    id:       'all',
    label:    'All Submissions',
    statuses: null,
    activeCls: 'bg-slate-700 text-white border-slate-600',
    countCls:  'bg-slate-600 text-slate-300',
  },
  {
    id:       'in_progress',
    label:    'In Progress',
    statuses: new Set(['NEW', 'READY_TO_SCHEDULE', 'SCHEDULED', 'SEEN']),
    activeCls: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/40',
    countCls:  'bg-indigo-500/30 text-indigo-300',
  },
  {
    id:       'completed',
    label:    'Completed',
    statuses: new Set(['CLOSED_LOOP']),
    activeCls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40',
    countCls:  'bg-emerald-500/30 text-emerald-300',
  },
];

const PAGE_SIZE = 25;

// ── Workflow status display config ────────────────────────────────────────────
const WF_STATUS_CFG = {
  NEW:               { label: 'New',                cls: 'bg-blue-500/20 text-blue-400'       },
  NEEDS_INFO:        { label: 'Needs Info',          cls: 'bg-amber-500/20 text-amber-400'     },
  READY_TO_SCHEDULE: { label: 'Ready to Schedule',   cls: 'bg-indigo-500/20 text-indigo-400'   },
  SCHEDULED:         { label: 'Scheduled',           cls: 'bg-teal-500/20 text-teal-400'       },
  SEEN:              { label: 'Seen',                cls: 'bg-violet-500/20 text-violet-400'   },
  CLOSED_LOOP:       { label: 'Closed Loop',         cls: 'bg-emerald-500/20 text-emerald-400' },
};

// ── Referral ID cell: full ID + copy-to-clipboard ────────────────────────────
const ReferralIdCell = ({ referralId }) => {
  const [copied, setCopied] = useState(false);

  if (!referralId) return <span className="text-slate-600">—</span>;

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(referralId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable — silent fail
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-slate-300 font-mono whitespace-nowrap">
        {referralId}
      </span>
      <button
        onClick={handleCopy}
        title={copied ? 'Copied!' : 'Copy Referral ID'}
        className={`p-0.5 rounded transition-all flex-shrink-0 ${
          copied ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'
        }`}
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      </button>
    </div>
  );
};

// ── On-Hold badge with hover tooltip ─────────────────────────────────────────
const OnHoldBadge = ({ reason }) => (
  <div className="relative group inline-flex">
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-400 whitespace-nowrap cursor-help">
      <PauseCircle className="w-3 h-3" />
      On Hold
    </span>
    {/* Tooltip */}
    <div className="
      pointer-events-none absolute bottom-full left-0 mb-2 z-50
      w-56 rounded-lg bg-slate-800 border border-slate-600/60 shadow-xl
      px-3 py-2.5
      opacity-0 group-hover:opacity-100 transition-opacity duration-150
    ">
      <p className="text-xs font-semibold text-amber-400 mb-1">On-hold reason</p>
      <p className="text-xs text-slate-300 leading-relaxed whitespace-normal">
        {reason ? `"${reason}"` : 'No reason recorded.'}
      </p>
    </div>
  </div>
);

// ============================================================================
// ERROR DISPLAY COMPONENTS
// ============================================================================

const IndexErrorMessage = () => (
  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 text-center">
    <AlertIcon className="w-12 h-12 text-amber-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-white mb-2">Setting Up Database</h3>
    <p className="text-slate-400 mb-4">
      This view requires a database index that is still being created.
      Please ask your administrator to complete setup and refresh in a minute.
    </p>
    <button
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 rounded-lg transition-colors"
    >
      <RefreshCw className="w-4 h-4 inline mr-2" />
      Refresh Page
    </button>
  </div>
);

const GenericErrorMessage = ({ error }) => (
  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
    <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
    <h3 className="text-lg font-medium text-white mb-2">Something went wrong</h3>
    <p className="text-slate-400 mb-4">
      {error || 'Unable to load your submissions. Please try again later.'}
    </p>
    <button
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
    >
      <RefreshCw className="w-4 h-4 inline mr-2" />
      Try Again
    </button>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const MySubmissionsView = ({ autoOpenReferralId, activeFocusTarget, onModalContext }) => {
  const hl = (target) => activeFocusTarget === target ? 'learning-focus-highlight' : '';
  const hlSubtle = (target) => activeFocusTarget === target ? 'learning-focus-highlight-subtle' : '';
  const { user } = useAuth();
  const [metrics,     setMetrics]     = useState([]);
  const [specialties, setSpecialties] = useState({});
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [isIndexError, setIsIndexError] = useState(false);
  const [dateRange,     setDateRange]     = useState('30d');
  const [outcomeFilter, setOutcomeFilter] = useState('');
  const [statusFilter,  setStatusFilter]  = useState('all');
  const [searchQuery,   setSearchQuery]   = useState('');

  // Full-account search: searching by Referral ID must scan ALL of the user's
  // submissions, not just the loaded page. When a query is present we page through
  // every result (same indexed createdByUid query) once and filter client-side.
  // { loading, rows, forOutcome } — rows=null until a scan completes.
  const [searchScan, setSearchScan] = useState({ loading: false, rows: null, forOutcome: null });

  // Pagination state
  const [currentPage,  setCurrentPage]  = useState(1);
  const [lastDoc,      setLastDoc]      = useState(null);
  const [hasMore,      setHasMore]      = useState(false);
  const [pageHistory,  setPageHistory]  = useState([]);

  // Receipt modal state
  const [receiptModal, setReceiptModal] = useState({
    open:           false,
    isLoading:      false,
    model:          null,
    workflowState:  null,
    workflowEvents: [],
    usersMap:       {},
    error:          null,
  });

  // Workflow state map: referralId → { status, onHold, onHoldReason }
  // Always re-fetched for every current page to avoid stale on-hold data.
  const [workflowMap, setWorkflowMap] = useState({});

  // ── Data loading ───────────────────────────────────────────────────────────

  const loadData = useCallback(async (cursor = null, isNewQuery = true) => {
    if (!user?.tenantId || (!user?.id && !user?.uid)) return;

    setLoading(true);
    setError(null);
    setIsIndexError(false);

    try {
      const userId = user.uid || user.id;

      console.log('[MySubmissions] Loading metrics for:', {
        tenantId: user.tenantId,
        userId,
        outcomeFilter,
        pageSize: PAGE_SIZE,
      });

      // Load specialties once per query reset
      if (isNewQuery) {
        const specialtiesData = await getSpecialties(user.tenantId);
        setSpecialties(specialtiesData || {});
      }

      // Load metrics with pagination
      const result = await getMetricsByUser(user.tenantId, userId, {
        pageSize: PAGE_SIZE,
        startAfter: cursor,
        outcome: outcomeFilter || null,
      });

      console.log('[MySubmissions] Metrics loaded:', {
        rowCount: result.rows?.length || 0,
        hasMore: result.hasMore,
      });

      setMetrics(result.rows || []);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Error loading submissions:', err);

      if (
        err.code === 'failed-precondition' ||
        err.message?.includes('requires an index') ||
        err.message?.includes('index')
      ) {
        setIsIndexError(true);
      } else {
        setError(safeUserError(err));
      }
    } finally {
      setLoading(false);
    }
  }, [user?.tenantId, user?.uid, user?.id, outcomeFilter]);

  // Initial load + reset on filter change
  useEffect(() => {
    setCurrentPage(1);
    setPageHistory([]);
    loadData(null, true);
  }, [outcomeFilter]);

  // ── Pagination ─────────────────────────────────────────────────────────────

  const handleNextPage = async () => {
    if (!hasMore || loading) return;
    setPageHistory(prev => [...prev, lastDoc]);
    setCurrentPage(prev => prev + 1);
    await loadData(lastDoc, false);
  };

  const handlePrevPage = async () => {
    if (currentPage <= 1 || loading) return;
    const newHistory = [...pageHistory];
    newHistory.pop();
    const prevCursor = newHistory.length > 0 ? newHistory[newHistory.length - 1] : null;
    setPageHistory(newHistory);
    setCurrentPage(prev => prev - 1);
    await loadData(prevCursor, false);
  };

  const handleFirstPage = async () => {
    if (currentPage <= 1 || loading) return;
    setPageHistory([]);
    setCurrentPage(1);
    await loadData(null, false);
  };

  // ── Full-account search scan ────────────────────────────────────────────────
  // When a Referral ID search is active, page through ALL of the user's metrics
  // (bounded) so the search covers every submission, not just the loaded page.
  // Results are cached for the active outcome filter and invalidated when the
  // query is cleared or the outcome filter changes.
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const searchActive = normalizedSearch.length > 0;

  useEffect(() => {
    if (!searchActive) {
      if (searchScan.rows !== null || searchScan.loading) {
        setSearchScan({ loading: false, rows: null, forOutcome: null });
      }
      return;
    }
    if (!user?.tenantId) return;
    const userId = user.uid || user.id;
    if (!userId) return;
    // Already have a complete scan for the current outcome filter.
    if (searchScan.rows !== null && searchScan.forOutcome === (outcomeFilter || null)) return;

    let cancelled = false;
    setSearchScan({ loading: true, rows: null, forOutcome: outcomeFilter || null });
    (async () => {
      const all = [];
      let cursor = null;
      const SCAN_PAGE = 200;
      const MAX_ROWS = 5000;       // safety cap
      const MAX_PAGES = 50;
      try {
        for (let i = 0; i < MAX_PAGES; i++) {
          const res = await getMetricsByUser(user.tenantId, userId, {
            pageSize: SCAN_PAGE,
            startAfter: cursor,
            outcome: outcomeFilter || null,
          });
          all.push(...(res.rows || []));
          if (!res.hasMore || !res.lastDoc || all.length >= MAX_ROWS) break;
          cursor = res.lastDoc;
        }
        if (!cancelled) setSearchScan({ loading: false, rows: all, forOutcome: outcomeFilter || null });
      } catch (err) {
        console.error('[MySubmissions] search scan failed:', err?.message || err);
        if (!cancelled) setSearchScan({ loading: false, rows: [], forOutcome: outcomeFilter || null });
      }
    })();
    return () => { cancelled = true; };
  }, [searchActive, outcomeFilter, user?.tenantId, user?.uid, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active row source: full scan while searching (once ready), else the loaded page.
  const searchReady = searchActive && searchScan.rows !== null;
  const activeSource = searchReady ? searchScan.rows : metrics;

  // ── Batch-fetch workflow states for the displayed rows ──────────────────────
  // Re-fetches ALL displayed IDs whenever the active source changes.
  // No dedup cache — ensures on-hold and status changes are always fresh.

  useEffect(() => {
    if (!user?.tenantId || activeSource.length === 0) return;

    const tenantId = user.tenantId;
    const ids = activeSource.map(m => m.referralId).filter(Boolean);
    if (ids.length === 0) return;

    // Reset map to undefined (skeleton state) for all current IDs before fetch
    setWorkflowMap(prev => {
      const next = { ...prev };
      ids.forEach(id => { if (next[id] === undefined) next[id] = undefined; });
      return next;
    });

    Promise.all(
      ids.map(referralId =>
        getWorkflowState(tenantId, referralId)
          .then(state => ({
            referralId,
            wf: {
              status:       state?.status       ?? null,
              onHold:       state?.onHold       === true,
              onHoldReason: state?.onHoldReason ?? null,
            },
          }))
          .catch(() => ({
            referralId,
            wf: { status: null, onHold: false, onHoldReason: null },
          }))
      )
    ).then(results => {
      setWorkflowMap(prev => {
        const next = { ...prev };
        results.forEach(({ referralId, wf }) => {
          next[referralId] = wf;
        });
        return next;
      });
    });
  }, [activeSource, user?.tenantId]);

  // ── Receipt modal ──────────────────────────────────────────────────────────

  /**
   * Called when user clicks the View (eye) icon for a metric row.
   * Loads full referral doc + pack + PHI in parallel, builds the receipt model,
   * and opens the modal.
   */
  const handleViewReceipt = async (metric) => {
    const referralId = metric.referralId;
    const tenantId   = user.tenantId;

    if (!referralId || !tenantId) {
      setReceiptModal({ open: true, isLoading: false, model: null, error: 'Missing referral ID.' });
      return;
    }

    // Open modal immediately in loading state
    setReceiptModal({ open: true, isLoading: true, model: null, error: null });
    onModalContext?.('receipt');

    try {
      // ── Parallel fetches ──
      const specialtyId = metric.specialtyId;
      const packId      = metric.packId;

      // Fetch workflowEvents for the receipt activity timeline
      const fetchWorkflowEvents = async () => {
        try {
          const db     = getFirestore(getApp());
          const colRef = collection(db, PATHS.col.workflowEvents(tenantId, referralId));
          const snap   = await getDocs(query(colRef, orderBy('createdAt', 'asc')));
          return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch {
          return [];
        }
      };

      const [referralDoc, packsData, workflowState, idRes, ctRes, insRes, attRes, workflowEvents] = await Promise.all([
        getReferral(tenantId, referralId),
        getPacks(tenantId, specialtyId).catch(() => ({})),
        getWorkflowState(tenantId, referralId).catch(() => null),
        getPatientIdentity(referralId).catch(() => null),
        getContact(referralId).catch(() => null),
        getInsurance(referralId).catch(() => ({ exists: false, data: null })),
        listAttachments(referralId).catch(() => ({ attachments: [] })),
        fetchWorkflowEvents(),
      ]);

      if (!referralDoc) {
        setReceiptModal({ open: true, isLoading: false, model: null, error: 'Referral document not found.' });
        return;
      }

      // ── Batch-resolve display names for all unique actorUids in the timeline ──
      const usersMap = {};
      const actorUids = [...new Set(workflowEvents.map(e => e.actorUid).filter(Boolean))];
      await Promise.all(
        actorUids.map(async uid => {
          try {
            const profile = await getTenantUser(tenantId, uid);
            if (profile) usersMap[uid] = profile;
          } catch {
            // name unavailable — will fall back to role label only
          }
        })
      );

      // ── Resolve pack ──
      const packList = Object.values(packsData || {});
      const packDoc = packList.find(p =>
        p.id === packId ||
        p.packId === packId ||
        p.metadata?.id === packId
      ) || packList[0] || null;

      const packFields   = packDoc?.fields   || [];
      const packRedFlags = packDoc?.redFlags  || [];
      const packName     = packDoc?.metadata?.name    || packId     || '—';
      const packVersion  = packDoc?.metadata?.version || metric.versionId || '—';

      // ── Map missing fields / red flags to labelled objects ──
      const missingFieldIds     = referralDoc.missingFieldIds    || [];
      const triggeredRedFlagIds = referralDoc.triggeredRedFlagIds || [];

      const missingFields = missingFieldIds
        .map(id => packFields.find(f => f.id === id))
        .filter(Boolean)
        .map(f => ({ id: f.id, label: f.label }));

      const triggeredRedFlags = triggeredRedFlagIds
        .map(id => packRedFlags.find(rf => rf.id === id))
        .filter(Boolean)
        .map(rf => ({ id: rf.id, label: rf.label }));

      const requiredFields = packFields
        .filter(f => f.required)
        .map(f => ({ id: f.id, label: f.label, required: true, helpText: f.helpText || '' }));

      // ── Specialty name ──
      const specialtyName =
        specialties[specialtyId]?.name ||
        referralDoc.specialtyId         ||
        specialtyId                      || '—';

      // ── Provider name (GP's own submissions — current user) ──
      const submittedByName =
        user.displayName || user.name || user.email || 'Unknown Provider';

      // ── PHI ──
      const phiIdentity = idRes?.data  || null;
      const phiContact  = ctRes?.data  || null;
      const phiInsurance = insRes?.data || null;
      const attachments  = attRes?.attachments || [];

      // ── Build model ──
      const model = buildFullReceiptModel({
        // Non-PHI
        runId:               metric.eventId || referralId,
        referralId,
        tenantId,
        specialtyName,
        packName,
        packVersion,
        outcome:             metric.outcomeStatus || referralDoc.finalOutcome || '',
        completenessScore:   metric.completenessScore ?? referralDoc.completenessScore ?? 0,
        visitType:           referralDoc.visitType || referralDoc.telehealthContext?.visitType || '',
        reasonForEscalation: referralDoc.reasonForEscalation || referralDoc.telehealthContext?.reasonForEscalation || '',
        missingFields,
        triggeredRedFlags,
        requiredFields,
        clinicalAnswers:     referralDoc.clinicalAnswers || referralDoc.answers || {},
        overrideInfo:        (() => {
                               // Primary: GP-initiated override stored on the referral doc itself
                               const docReason =
                                 referralDoc.outcome?.overrideReason ||
                                 referralDoc.override?.reason        || '';
                               if (docReason) {
                                 return {
                                   reason: docReason,
                                   note:   referralDoc.override?.note || referralDoc.outcome?.reason || '',
                                 };
                               }
                               // Fallback: triage override stored on workflow/state
                               if (workflowState?.overrideReason) {
                                 return {
                                   reason: workflowState.overrideReason,
                                   note:   workflowState.overrideNote || '',
                                 };
                               }
                               return null;
                             })(),
        submittedAt:         referralDoc.submittedAt || referralDoc.createdAt || metric.savedAt,
        submittedByName,
        submittedByRole:     'gp_employee',
        // PHI
        phiIdentity,
        phiContact,
        phiInsurance,
        attachments,
      });

      setReceiptModal({ open: true, isLoading: false, model, workflowState, workflowEvents, usersMap, error: null });

    } catch (err) {
      console.error('[MySubmissions] Receipt load failed:', err);
      setReceiptModal({
        open: true,
        isLoading: false,
        model: null,
        error: safeUserError(err),
      });
    }
  };

  const handleCloseReceipt = () => {
    setReceiptModal({ open: false, isLoading: false, model: null, error: null });
    onModalContext?.(null);
  };

  // When arriving from a Referral Ping "Open Referral", scope the list to the exact
  // referral: widen the date/status filters (so it can't be hidden) and prefill the
  // referral-ID search. Fires once per autoOpenReferralId.
  const autoFilterFired = useRef(false);
  useEffect(() => {
    if (!autoOpenReferralId || autoFilterFired.current) return;
    autoFilterFired.current = true;
    setDateRange('all');
    setStatusFilter('all');
    setSearchQuery(autoOpenReferralId);
  }, [autoOpenReferralId]);

  // Best-effort: also open the receipt automatically once the matching row is loaded.
  const autoOpenFired = useRef(false);
  useEffect(() => {
    if (!autoOpenReferralId || autoOpenFired.current || loading || metrics.length === 0) return;
    const match = metrics.find(m => m.referralId === autoOpenReferralId);
    if (!match) return;
    autoOpenFired.current = true;
    handleViewReceipt(match);
  }, [autoOpenReferralId, loading, metrics]);

  // ── Date filter (client-side) ──────────────────────────────────────────────

  const getDateFilteredMetrics = () => {
    let filtered = [...activeSource];
    const preset = DATE_RANGES.find(r => r.id === dateRange);
    if (preset && preset.days !== null) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - preset.days);
      cutoff.setHours(0, 0, 0, 0);
      filtered = filtered.filter(m => {
        const ts = m.savedAt?.toDate?.() || new Date(m.savedAt || m.timestamp);
        return ts >= cutoff;
      });
    }
    return filtered;
  };

  // Date-filtered base (used for tab counts + further status filtering)
  const dateFilteredBase = getDateFilteredMetrics();

  // Apply referral ID search filter (normalizedSearch computed above).
  const dateFilteredMetrics = searchActive
    ? dateFilteredBase.filter(m =>
        m.referralId && m.referralId.toLowerCase().includes(normalizedSearch)
      )
    : dateFilteredBase;

  // Tab counts — computed from date-filtered metrics via workflowMap
  const tabCounts = {};
  STATUS_TABS.forEach(tab => {
    if (!tab.statuses) {
      tabCounts[tab.id] = dateFilteredMetrics.length;
    } else {
      tabCounts[tab.id] = dateFilteredMetrics.filter(m => {
        const wf = workflowMap[m.referralId];
        return wf !== undefined && tab.statuses.has(wf?.status);
      }).length;
    }
  });

  // Final filtered list: date + status tab. While searching we span all statuses
  // (the Referral ID search is status-agnostic and covers every page).
  const activeTab = STATUS_TABS.find(t => t.id === statusFilter);
  const filteredMetrics = (!searchActive && activeTab?.statuses)
    ? dateFilteredMetrics.filter(m => {
        const wf = workflowMap[m.referralId];
        return wf !== undefined && activeTab.statuses.has(wf?.status);
      })
    : dateFilteredMetrics;

  // ── Formatters ─────────────────────────────────────────────────────────────

  const formatDate = (ts) => {
    if (!ts) return '-';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    return date.toLocaleDateString(undefined, {
      month:  'short',
      day:    'numeric',
      hour:   '2-digit',
      minute: '2-digit',
    });
  };

  const getSpecialtyName = (specialtyId) =>
    specialties[specialtyId]?.name || specialtyId || 'Unknown';

  const getOutcomeBadge = (outcomeStatus) => {
    const status = (outcomeStatus || '').toLowerCase();
    switch (status) {
      case 'routine':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Routine
          </span>
        );
      case 'urgent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-medium">
            <AlertTriangle className="w-3 h-3" />
            Urgent
          </span>
        );
      case 'not_yet':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Not Yet
          </span>
        );
      default:
        return <span className="text-slate-500">{outcomeStatus || '-'}</span>;
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-4 md:p-8">

      {/* Header */}
      <div className={`mb-6 md:mb-8 ${hlSubtle('my_submissions_page_header')}`}>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
          <FileText className="w-4 h-4 text-emerald-400" />
          <span className="text-xs font-medium text-emerald-400 uppercase">My Submissions</span>
        </div>
        <h1 className="text-xl md:text-2xl font-semibold text-white mb-2">Your Referral History</h1>
        <p className="text-slate-400">Track all referrals you've submitted</p>
      </div>

      {/* Error States */}
      {isIndexError ? (
        <IndexErrorMessage />
      ) : error ? (
        <GenericErrorMessage error={error} />
      ) : (
        <>
          {/* ── Search by Referral ID ── */}
          <div className={`relative mb-4 ${hl('my_submissions_filters')}`}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Referral ID..."
              className="w-full sm:w-80 pl-10 pr-9 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-xl text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* ── Pipeline Filter Tabs ── */}
          <div className={`flex flex-wrap gap-2 mb-4 ${hl('my_submissions_tabs')}`}>
            {STATUS_TABS.map(tab => {
              const isActive = statusFilter === tab.id;
              const count    = tabCounts[tab.id] ?? 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`
                    inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border
                    transition-colors
                    ${isActive
                      ? tab.activeCls
                      : 'bg-slate-800/40 text-slate-400 border-slate-700/50 hover:text-white hover:border-slate-600'
                    }
                  `}
                >
                  {tab.label}
                  <span className={`
                    text-xs px-1.5 py-0.5 rounded-full font-semibold tabular-nums
                    ${isActive ? tab.countCls : 'bg-slate-700/60 text-slate-500'}
                  `}>
                    {loading ? '…' : count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ── Secondary Filters (date + outcome) ── */}
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            {/* Date Range */}
            <div className="flex items-center gap-2 flex-wrap">
              <Calendar className="w-4 h-4 text-slate-500 hidden sm:block" />
              {DATE_RANGES.map(range => (
                <button
                  key={range.id}
                  onClick={() => setDateRange(range.id)}
                  className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    dateRange === range.id
                      ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                      : 'bg-slate-800/50 text-slate-400 hover:text-white border border-slate-700/50'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>

            {/* Outcome Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500 hidden sm:block" />
              <select
                value={outcomeFilter}
                onChange={(e) => setOutcomeFilter(e.target.value)}
                className="bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-sm text-slate-300 focus:outline-none focus:border-emerald-500/50"
              >
                {OUTCOME_FILTERS.map(opt => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Submissions List */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
            {loading || (searchActive && searchScan.loading) ? (
              <div className="p-12 text-center">
                <div className="animate-spin w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full mx-auto" />
                <p className="text-slate-500 mt-4">
                  {searchActive && searchScan.loading ? 'Searching all submissions…' : 'Loading submissions...'}
                </p>
              </div>
            ) : filteredMetrics.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">No submissions found</p>
                <p className="text-sm text-slate-500 mt-1">
                  {searchActive
                    ? `No referral matches “${searchQuery.trim()}” in your submissions`
                    : statusFilter !== 'all'
                    ? `No referrals match the "${activeTab?.label}" filter`
                    : outcomeFilter
                    ? 'Try changing your filters'
                    : 'Submit a referral to see it here'}
                </p>
              </div>
            ) : (
              <>
                {/* ── Desktop Table ── */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead className={`bg-slate-900/50 ${hlSubtle('my_submissions_table')}`}>
                      <tr>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Date</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-4 py-3">Referral ID</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Specialty / Pack</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Outcome</th>
                        <th className="text-left text-xs font-medium text-slate-500 uppercase px-6 py-3">Status</th>
                        <th className={`text-center text-xs font-medium text-slate-500 uppercase px-4 py-3 ${hl('my_submissions_view_action')}`}>View</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                      {filteredMetrics.map((metric, idx) => (
                        <tr key={metric.eventId || idx} className="hover:bg-slate-800/30">
                          <td className="px-6 py-4 text-sm text-slate-400 whitespace-nowrap">
                            {formatDate(metric.savedAt || metric.timestamp)}
                          </td>
                          <td className="px-4 py-4">
                            <ReferralIdCell referralId={metric.referralId} />
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-white font-medium">{getSpecialtyName(metric.specialtyId)}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              {metric.packId}{' '}
                              <span className="text-slate-600">v{metric.versionId}</span>
                            </p>
                          </td>
                          <td className="px-6 py-4">
                            {getOutcomeBadge(metric.outcomeStatus)}
                          </td>
                          <td className="px-6 py-4">
                            {(() => {
                              const wf = workflowMap[metric.referralId];
                              // Still loading — show skeleton
                              if (wf === undefined) {
                                return (
                                  <div className="h-5 w-28 bg-slate-700/60 rounded-full animate-pulse" />
                                );
                              }
                              const cfg = wf?.status ? WF_STATUS_CFG[wf.status] : null;
                              return (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {wf?.onHold && (
                                    <OnHoldBadge reason={wf.onHoldReason} />
                                  )}
                                  {cfg
                                    ? (
                                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${cfg.cls}`}>
                                        {cfg.label}
                                      </span>
                                    )
                                    : <span className="text-slate-600 text-xs">—</span>
                                  }
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => handleViewReceipt(metric)}
                              title="View referral receipt"
                              className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-700/60 text-slate-400 hover:text-emerald-400 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ── Mobile Cards ── */}
                <div className="md:hidden divide-y divide-slate-700/50">
                  {filteredMetrics.map((metric, idx) => (
                    <div key={metric.eventId || idx} className="p-4">
                      {/* Top row: date + outcome + view button */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-400">
                            {formatDate(metric.savedAt || metric.timestamp)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getOutcomeBadge(metric.outcomeStatus)}
                          <button
                            onClick={() => handleViewReceipt(metric)}
                            title="View referral receipt"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg hover:bg-slate-700/60 text-slate-400 hover:text-emerald-400 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Referral ID */}
                      {metric.referralId && (
                        <div className="flex items-center gap-1 mb-1">
                          <span className="text-xs text-slate-600">ID:</span>
                          <ReferralIdCell referralId={metric.referralId} />
                        </div>
                      )}

                      {/* Specialty / Pack */}
                      <p className="text-white font-medium mb-0.5">
                        {getSpecialtyName(metric.specialtyId)}
                      </p>
                      <p className="text-xs text-slate-500 mb-2">
                        {metric.packId}{' '}
                        <span className="text-slate-600">v{metric.versionId}</span>
                      </p>

                      {/* Referral status */}
                      {(() => {
                        const wf = workflowMap[metric.referralId];
                        if (wf === undefined) {
                          return <div className="h-5 w-32 bg-slate-700/60 rounded-full animate-pulse mt-1" />;
                        }
                        const cfg = wf?.status ? WF_STATUS_CFG[wf.status] : null;
                        return (
                          <div className="flex items-center gap-1.5 flex-wrap mt-1">
                            {wf?.onHold && (
                              <OnHoldBadge reason={wf.onHoldReason} />
                            )}
                            {cfg && (
                              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.cls}`}>
                                {cfg.label}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>

                {/* Pagination — hidden while searching (results are a full-account scan) */}
                {!searchActive && (
                  <div className="p-4 border-t border-slate-700/50">
                    <PaginationControls
                      currentPage={currentPage}
                      hasMore={hasMore}
                      hasPrev={currentPage > 1}
                      onNextPage={handleNextPage}
                      onPrevPage={handlePrevPage}
                      onFirstPage={handleFirstPage}
                      loading={loading}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* Receipt Modal */}
      {receiptModal.open && (
        <ReceiptModal
          model={receiptModal.model}
          isLoading={receiptModal.isLoading}
          error={receiptModal.error}
          workflowState={receiptModal.workflowState}
          workflowEvents={receiptModal.workflowEvents}
          usersMap={receiptModal.usersMap}
          onClose={handleCloseReceipt}
          activeFocusTarget={activeFocusTarget}
        />
      )}

    </div>
  );
};

export default MySubmissionsView;
